import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle, RefreshCw, Wrench, MapPin, Users, Briefcase, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/utils/api';

const LocationIntegrity = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);
  const [integrityData, setIntegrityData] = useState(null);
  const [unassignedDistricts, setUnassignedDistricts] = useState(null);

  useEffect(() => {
    fetchIntegrityData();
  }, []);

  const fetchIntegrityData = async () => {
    setLoading(true);
    try {
      const [integrityRes, unassignedRes] = await Promise.all([
        api.get('/location-master/integrity-check'),
        api.get('/location-master/unassigned-districts')
      ]);
      setIntegrityData(integrityRes.data);
      setUnassignedDistricts(unassignedRes.data);
    } catch (error) {
      toast.error('Failed to fetch integrity data');
    } finally {
      setLoading(false);
    }
  };

  const fixOrphanDistricts = async () => {
    setFixing(true);
    try {
      const response = await api.post('/location-master/fix-orphan-districts');
      toast.success(response.data.message);
      fetchIntegrityData();
    } catch (error) {
      toast.error('Failed to fix orphan districts');
    } finally {
      setFixing(false);
    }
  };

  const fixOrphanCities = async () => {
    setFixing(true);
    try {
      const response = await api.post('/location-master/fix-orphan-cities');
      toast.success(response.data.message);
      fetchIntegrityData();
    } catch (error) {
      toast.error('Failed to fix orphan cities');
    } finally {
      setFixing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Checking location data integrity...</p>
      </div>
    );
  }

  const hasIssues = integrityData?.summary?.orphan_districts > 0 || integrityData?.summary?.orphan_cities > 0;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/location-masters')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
              Location Data Integrity
            </h1>
            <p className="text-slate-600">Check and fix location data mismatches between jobs and master data</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchIntegrityData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Status Banner */}
      <Card className={`p-4 mb-6 ${hasIssues ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
        <div className="flex items-center gap-3">
          {hasIssues ? (
            <>
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-800">Data Integrity Issues Found</p>
                <p className="text-sm text-amber-700">
                  {integrityData?.summary?.orphan_districts} district(s) and {integrityData?.summary?.orphan_cities} city/cities in jobs are not in master data.
                  Jobs with these locations won't be visible to executives.
                </p>
              </div>
            </>
          ) : (
            <>
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-800">All Location Data is Synced</p>
                <p className="text-sm text-green-700">All job locations match the master data.</p>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-blue-500" />
            <div>
              <p className="text-xs text-slate-500">Job Locations</p>
              <p className="text-xl font-bold text-slate-900">{integrityData?.summary?.total_job_locations || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-teal-500" />
            <div>
              <p className="text-xs text-slate-500">Master Districts</p>
              <p className="text-xl font-bold text-slate-900">{integrityData?.summary?.total_master_districts || 0}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-purple-500" />
            <div>
              <p className="text-xs text-slate-500">Master Cities</p>
              <p className="text-xl font-bold text-slate-900">{integrityData?.summary?.total_master_cities || 0}</p>
            </div>
          </div>
        </Card>
        <Card className={`p-4 ${integrityData?.summary?.orphan_districts > 0 ? 'bg-red-50 border-red-200' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-5 h-5 ${integrityData?.summary?.orphan_districts > 0 ? 'text-red-500' : 'text-slate-400'}`} />
            <div>
              <p className="text-xs text-slate-500">Orphan Districts</p>
              <p className={`text-xl font-bold ${integrityData?.summary?.orphan_districts > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                {integrityData?.summary?.orphan_districts || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card className={`p-4 ${integrityData?.summary?.orphan_cities > 0 ? 'bg-amber-50 border-amber-200' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-5 h-5 ${integrityData?.summary?.orphan_cities > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
            <div>
              <p className="text-xs text-slate-500">Orphan Cities</p>
              <p className={`text-xl font-bold ${integrityData?.summary?.orphan_cities > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
                {integrityData?.summary?.orphan_cities || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Orphan Districts Section */}
      {integrityData?.orphan_districts?.length > 0 && (
        <Card className="mb-6 border-red-200">
          <div className="p-4 border-b border-red-100 bg-red-50 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-red-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Orphan Districts ({integrityData.orphan_districts.length})
              </h2>
              <p className="text-sm text-red-700">
                These districts exist in job postings but NOT in district master. Jobs won't be visible to any executive.
              </p>
            </div>
            <Button 
              onClick={fixOrphanDistricts} 
              disabled={fixing}
              className="bg-red-600 hover:bg-red-700"
            >
              <Wrench className={`w-4 h-4 mr-2 ${fixing ? 'animate-spin' : ''}`} />
              Fix All ({integrityData.orphan_districts.length})
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>District</TableHead>
                <TableHead className="text-center">Jobs Affected</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {integrityData.orphan_districts.map((loc, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{loc.state}</TableCell>
                  <TableCell>{loc.district}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="bg-red-100 text-red-700">{loc.job_count} jobs</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-red-100 text-red-700">
                      Not in Master
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Orphan Cities Section */}
      {integrityData?.orphan_cities?.length > 0 && (
        <Card className="mb-6 border-amber-200">
          <div className="p-4 border-b border-amber-100 bg-amber-50 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Orphan Cities ({integrityData.orphan_cities.length})
              </h2>
              <p className="text-sm text-amber-700">
                These cities exist in job postings but NOT in city master.
              </p>
            </div>
            <Button 
              onClick={fixOrphanCities} 
              disabled={fixing}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Wrench className={`w-4 h-4 mr-2 ${fixing ? 'animate-spin' : ''}`} />
              Fix All ({integrityData.orphan_cities.length})
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>State</TableHead>
                <TableHead>District</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-center">Jobs Affected</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {integrityData.orphan_cities.map((loc, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{loc.state}</TableCell>
                  <TableCell>{loc.district}</TableCell>
                  <TableCell>{loc.city}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="outline" className="bg-amber-100 text-amber-700">{loc.job_count} jobs</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Unassigned Districts Section */}
      {unassignedDistricts?.total_with_jobs > 0 && (
        <Card className="mb-6 border-blue-200">
          <div className="p-4 border-b border-blue-100 bg-blue-50">
            <h2 className="font-semibold text-blue-800 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Districts Not Assigned to Any Executive ({unassignedDistricts.total_with_jobs} with jobs)
            </h2>
            <p className="text-sm text-blue-700">
              These districts exist in master but are not assigned to any executive. Jobs in these districts won't be managed.
            </p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>State</TableHead>
                  <TableHead>District</TableHead>
                  <TableHead className="text-center">Jobs in District</TableHead>
                  <TableHead>Action Required</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unassignedDistricts.districts
                  .filter(d => d.job_count > 0)
                  .map((loc, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{loc.state}</TableCell>
                    <TableCell>{loc.district}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="bg-blue-100 text-blue-700">{loc.job_count} jobs</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-orange-100 text-orange-700">
                        Assign to Executive
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {unassignedDistricts.total_unassigned > unassignedDistricts.total_with_jobs && (
            <div className="p-3 border-t bg-slate-50 text-sm text-slate-600">
              + {unassignedDistricts.total_unassigned - unassignedDistricts.total_with_jobs} more districts with 0 jobs
            </div>
          )}
        </Card>
      )}

      {/* All Good Message */}
      {!hasIssues && unassignedDistricts?.total_with_jobs === 0 && (
        <Card className="p-8 text-center border-green-200 bg-green-50">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-green-800 mb-2">All Location Data is Synced!</h2>
          <p className="text-green-700">
            All job locations match the master data and all districts with jobs are assigned to executives.
          </p>
        </Card>
      )}
    </div>
  );
};

export default LocationIntegrity;
